import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { ApiError } from '@libs/errors';
import useLockBodyScroll from '@libs/hooks/useLockBodyScroll';
import { getLocalStorage } from '@libs/storage';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { useAnonymousSecret } from '@features/auth/hooks/useAnonymousSecret';
import { useAuth } from '@features/auth/hooks/useAuth';
import { IPoolType } from '@features/game/types/fast-game';
import { useCreateInviteMutation, useMyInvitesQuery } from '@features/shared/api/fastgames';
import { useDeviceId } from '@features/shared/hooks/useDeviceId';

import { CreateFriendGameModal } from './CreateFriendGameModal';
import { ITimeControlOption, TIME_CONTROLS } from './types';

const STORAGE_KEY = 'friend-game-tc';

export function CreateFriendGameModalWidget({
  className,
  closeText,
  initialPool,
  onClose,
}: {
  className?: string;
  closeText?: string;
  initialPool: IPoolType | undefined;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const mediaQuery = useMediaQuery();
  const isMobile = mediaQuery === 'mobile' || mediaQuery === 'tablet';
  const { isAuthenticated } = useAuth();

  useLockBodyScroll(isMobile);

  const deviceId = useDeviceId();
  const anonymousSecret = useAnonymousSecret(isAuthenticated);

  const myInvitesQuery = useMyInvitesQuery(deviceId, anonymousSecret);
  const createMutation = useCreateInviteMutation(deviceId, anonymousSecret);

  const activeInvites = myInvitesQuery.data?.invites;

  const result = createMutation.data;

  useEffect(() => {
    if (!result) return;

    navigate({ to: '/game/friend/$gameId', params: { gameId: result.id } });
  }, [navigate, result]);

  const refetchMyInvites = myInvitesQuery.refetch;
  useEffect(() => {
    if (createMutation.error instanceof ApiError && createMutation.error.isBadRequest) {
      refetchMyInvites();
    }
  }, [createMutation.error, refetchMyInvites]);

  const [initialOption] = useState<ITimeControlOption>(() => {
    const id = initialPool ?? loadPoolId();
    return TIME_CONTROLS.find((tc) => tc.id === id) ?? TIME_CONTROLS[0]!;
  });

  return (
    <CreateFriendGameModal
      className={className}
      isMobile={isMobile}
      isAuthenticated={isAuthenticated}
      initialPool={initialOption}
      onPlay={(cfg, pool) => {
        savePoolId(pool.id);
        const m = createMutation.mutate;
        m(cfg);
      }}
      playError={createMutation.error}
      onMyGames={activeInvites?.length ? () => navigate({ to: '/game/friend/games' }) : undefined}
      myGamesCount={activeInvites?.length}
      closeText={closeText}
      onClose={onClose}
    />
  );
}

function loadPoolId(): IPoolType | null {
  return getLocalStorage().getItem(STORAGE_KEY) as IPoolType | null;
}

function savePoolId(id: IPoolType) {
  getLocalStorage().setItem(STORAGE_KEY, id);
}
